import apiClient from "./apiClient";

export type ApplicationStatus =
  | "interested"
  | "saved"
  | "researching"
  | "planning-to-apply"
  | "applied"
  | "waitlisted"
  | "accepted"
  | "rejected"
  | "withdrawn"
  | "payment-due"
  | "confirmed"
  | "attended"
  | "skipped"
  | "cancelled";

export interface EventOpportunity {
  _id: string;
  id?: string;
  name: string;
  description?: string;
  summary?: string;
  imageUrl?: string;
  startAt: string;
  endAt?: string;
  venueName?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  indoorOutdoor?: string;
  /** Includes `schedule` for imported listings (days/hours text). */
  requirements?: Record<string, unknown>;
  status?: string;
  /** False when the event has ended or has no known dates yet. */
  datesConfirmed?: boolean;
  categories: string[];
  vendorCategories: string[];
  productRestrictions?: string[];
  organizerName?: string;
  officialWebsite?: string;
  applicationStatus: string;
  applicationDeadline?: string;
  applicationUrl?: string;
  boothFeeMin?: number;
  boothFeeMax?: number;
  applicationFee?: number;
  currency?: string;
  estimatedAttendance?: number;
  publicEmail?: string;
  publicPhone?: string;
  verificationStatus: string;
  freshnessStatus: string;
  confidenceScore: number;
  lastCheckedAt?: string;
  lastVerifiedAt?: string;
  sourceCount: number;
  distanceMiles?: number;
  match?: {
    score: number;
    confidence: string;
    primaryReason: string;
    positiveFactors: string[];
    negativeFactors: string[];
    missingInformation: string[];
    components: Array<{
      key: string;
      label: string;
      points: number;
      max: number;
    }>;
  };
  userState?: {
    saved: boolean;
    status: ApplicationStatus;
    privateNotes?: string;
    linkedEventId?: string;
  } | null;
  reviewSummary?: { count: number; averageRating?: number };
  sources?: Array<{
    _id: string;
    sourceType: string;
    url: string;
    title?: string;
    lastSuccessfulFetchAt?: string;
  }>;
  missingInformation?: string[];
}

export const searchEventOpportunities = async (
  params: Record<string, unknown>,
) => {
  const { data } = await apiClient.get<{
    items: EventOpportunity[];
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  }>("/event-opportunities", { params });
  return data;
};

/** Saves the user's home ZIP; the server rejects an unknown ZIP with a message. */
export const updateHomeZip = (zip: string) =>
  apiClient
    .put<{ homeZip: string }>("/auth/me/home-zip", { zip })
    .then(({ data }) => data);

export const getEventOpportunity = async (id: string) => {
  const { data } = await apiClient.get<EventOpportunity>(
    `/event-opportunities/${id}`,
  );
  return data;
};

export const saveEventOpportunity = (id: string) =>
  apiClient.post(`/event-opportunities/${id}/save`).then(({ data }) => data);

export const unsaveEventOpportunity = (id: string) =>
  apiClient.delete(`/event-opportunities/${id}/save`);

export const updateEventOpportunityState = (
  id: string,
  payload: { status: ApplicationStatus },
) =>
  apiClient
    .patch(`/event-opportunities/${id}/state`, payload)
    .then(({ data }) => data);

export const updateEventOpportunityNote = (id: string, privateNotes: string) =>
  apiClient
    .put(`/event-opportunities/${id}/private-note`, { privateNotes })
    .then(({ data }) => data);

export const submitEventOpportunityUrl = (url: string) =>
  apiClient
    .post("/event-opportunities/submit-url", { url })
    .then(({ data }) => data);

export const submitManualEventOpportunity = (
  payload: Record<string, unknown>,
) =>
  apiClient
    .post("/event-opportunities/submit-manual", payload)
    .then(({ data }) => data);

export const listEventOpportunityReviews = (id: string) =>
  apiClient
    .get<{ items: any[] }>(`/event-opportunities/${id}/reviews`)
    .then(({ data }) => data.items);

export const createEventOpportunityReview = (
  id: string,
  payload: Record<string, unknown>,
) =>
  apiClient
    .post(`/event-opportunities/${id}/reviews`, payload)
    .then(({ data }) => data);

export const reportEventOpportunity = (
  id: string,
  payload: { reason: string; detail?: string },
) =>
  apiClient
    .post(`/event-opportunities/${id}/report`, payload)
    .then(({ data }) => data);

export const convertEventOpportunity = (
  id: string,
  payload: {
    title: string;
    location: string;
    /** The day(s) the vendor will work; a listing can be a whole season. */
    dailySchedule?: Array<{
      date: string;
      startTime: string;
      endTime: string;
      startDateTime: string;
      endDateTime: string;
      hours: number;
    }>;
  },
) =>
  apiClient
    .post<{ event: { _id: string } }>(
      `/event-opportunities/${id}/create-internal-event`,
      payload,
    )
    .then(({ data }) => data);
